import React from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Building2, Megaphone, ArrowRight, CheckCircle, BarChart3, Shield, Zap } from 'lucide-react'

function Logo() {
  return (
    <img src="/logo.png" alt="AdsLocate" style={{ height: 56, width: 'auto' }} />
  )
}

export default function HomePage() {
  const navigate = useNavigate()

  return (
    <div style={{ minHeight: '100vh', background: 'white', fontFamily: 'var(--font-body)' }}>

      {/* ── Navbar ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(13,27,62,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '0 40px', height: 68,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Logo />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/login')} style={{
            background: 'none', border: '1px solid rgba(255,255,255,0.2)',
            color: 'rgba(255,255,255,0.8)', padding: '8px 20px',
            borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-display)',
            fontWeight: 600, fontSize: '0.88rem', transition: 'all 0.2s'
          }}
            onMouseEnter={e => e.target.style.borderColor = 'rgba(255,255,255,0.5)'}
            onMouseLeave={e => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
          >
            Sign In
          </button>
          <button onClick={() => navigate('/register')} style={{
            background: 'var(--tech)', border: 'none',
            color: 'white', padding: '8px 20px',
            borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-display)',
            fontWeight: 600, fontSize: '0.88rem', transition: 'all 0.2s'
          }}
            onMouseEnter={e => e.target.style.background = 'var(--tech-light)'}
            onMouseLeave={e => e.target.style.background = 'var(--tech)'}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--navy) 0%, #1a3a6b 60%, #0f2a52 100%)',
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden', paddingTop: 68
      }}>
        {/* Background decoration */}
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute', borderRadius: '50%',
            background: `rgba(48,102,190,${0.04 + i * 0.02})`,
            width: `${300 + i * 150}px`, height: `${300 + i * 150}px`,
            top: `${20 + i * 10}%`, right: `${-5 + i * 5}%`,
            pointerEvents: 'none'
          }} />
        ))}
        <div style={{
          position: 'absolute', bottom: '5%', left: '3%',
          fontSize: '16rem', opacity: 0.03, userSelect: 'none',
          fontFamily: 'var(--font-display)', fontWeight: 900, color: 'white'
        }}>AL</div>

        <div style={{ maxWidth: 860, textAlign: 'center', padding: '80px 24px', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(0,194,255,0.1)', border: '1px solid rgba(0,194,255,0.2)',
            borderRadius: 99, padding: '6px 16px', marginBottom: 28
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 2s ease infinite' }} />
            <span style={{ color: 'var(--accent)', fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.06em' }}>
              Dubai's #1 Outdoor Advertising Marketplace
            </span>
          </div>

          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(2.4rem, 6vw, 4rem)',
            fontWeight: 900, color: 'white', lineHeight: 1.1, marginBottom: 24,
            letterSpacing: '-0.03em'
          }}>
            Find the Perfect<br />
            <span style={{ color: 'var(--accent)' }}>Ad Space</span> for Your Brand
          </h1>

          <p style={{
            fontSize: '1.1rem', color: 'rgba(255,255,255,0.65)',
            maxWidth: 580, margin: '0 auto 40px', lineHeight: 1.7
          }}>
            Connect with premium billboard owners, digital screens, and street advertising across Dubai.
            Book in minutes, not weeks.
          </p>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')} style={{
              background: 'linear-gradient(135deg, var(--tech), var(--accent))',
              color: 'white', border: 'none', padding: '14px 32px',
              borderRadius: 10, cursor: 'pointer', fontFamily: 'var(--font-display)',
              fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: '0 8px 32px rgba(0,194,255,0.3)', transition: 'all 0.2s'
            }}>
              Start Advertising <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate('/register')} style={{
              background: 'rgba(255,255,255,0.08)', color: 'white',
              border: '1px solid rgba(255,255,255,0.2)', padding: '14px 32px',
              borderRadius: 10, cursor: 'pointer', fontFamily: 'var(--font-display)',
              fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8,
              transition: 'all 0.2s'
            }}>
              <Building2 size={18} /> List Your Space
            </button>
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex', gap: 40, justifyContent: 'center', marginTop: 60,
            flexWrap: 'wrap'
          }}>
            {[
              ['500+', 'Ad Spaces'],
              ['200+', 'Advertisers'],
              ['50+', 'Media Owners'],
              ['AED 0', 'To List'],
            ].map(([value, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 900, color: 'white' }}>{value}</div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How it works ── */}
      <div style={{ padding: '100px 24px', background: 'var(--gray-50)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--tech)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>How It Works</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', fontWeight: 800, color: 'var(--navy)' }}>
              Book an Ad Space in 3 Steps
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
            {[
              { step: '01', icon: <MapPin size={28} />, title: 'Find Your Location', desc: 'Browse hundreds of premium ad spaces across Dubai on our interactive map. Filter by area, size, price and type.' },
              { step: '02', icon: <Megaphone size={28} />, title: 'Submit Your Request', desc: 'Select your dates, upload your artwork, and send a booking request. No upfront payment required.' },
              { step: '03', icon: <CheckCircle size={28} />, title: 'Go Live', desc: 'Once approved, your ad goes up. We handle the coordination between you and the media owner.' },
            ].map((item) => (
              <div key={item.step} style={{
                background: 'white', borderRadius: 16, padding: 32,
                border: '1px solid var(--gray-100)', position: 'relative',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  position: 'absolute', top: 24, right: 24,
                  fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 900,
                  color: 'var(--gray-100)', lineHeight: 1
                }}>{item.step}</div>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: 'linear-gradient(135deg, var(--navy), var(--royal))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', marginBottom: 20
                }}>{item.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 700, marginBottom: 10, color: 'var(--navy)' }}>{item.title}</h3>
                <p style={{ color: 'var(--gray-500)', lineHeight: 1.7, fontSize: '0.92rem' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── For Advertisers & Media Owners ── */}
      <div style={{ padding: '100px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
          {/* Advertisers */}
          <div style={{
            background: 'var(--navy)', borderRadius: 20, padding: 40, color: 'white', position: 'relative', overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', bottom: -20, right: -20, fontSize: '8rem', opacity: 0.06 }}>🎯</div>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <Megaphone size={24} color="white" />
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 800, marginBottom: 12 }}>For Advertisers</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: 24, fontSize: '0.92rem' }}>
              Find the right location for your brand. Compare prices, impressions, and availability across Dubai in one place.
            </p>
            {['Browse 500+ premium locations', 'See daily impressions data', 'Book in minutes, not weeks', 'Upload artwork directly'].map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)' }}>
                <CheckCircle size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} /> {f}
              </div>
            ))}
            <button onClick={() => navigate('/register')} style={{
              marginTop: 24, background: 'var(--tech)', color: 'white', border: 'none',
              padding: '12px 24px', borderRadius: 8, cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', gap: 6
            }}>
              Find Ad Spaces <ArrowRight size={16} />
            </button>
          </div>

          {/* Media Owners */}
          <div style={{
            background: 'var(--gray-50)', borderRadius: 20, padding: 40, position: 'relative', overflow: 'hidden',
            border: '1px solid var(--gray-100)'
          }}>
            <div style={{ position: 'absolute', bottom: -20, right: -20, fontSize: '8rem', opacity: 0.04 }}>🏢</div>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <Building2 size={24} color="white" />
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 800, marginBottom: 12, color: 'var(--navy)' }}>For Media Owners</h3>
            <p style={{ color: 'var(--gray-500)', lineHeight: 1.7, marginBottom: 24, fontSize: '0.92rem' }}>
              Monetize your ad spaces by listing them on AdsLocate. We bring advertisers directly to you — no cold calls needed.
            </p>
            {['Free to list your spaces', 'Receive booking requests', 'Approve artwork before it goes live', 'Only 10% commission on bookings'].map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, fontSize: '0.88rem', color: 'var(--gray-600)' }}>
                <CheckCircle size={15} style={{ color: 'var(--success)', flexShrink: 0 }} /> {f}
              </div>
            ))}
            <button onClick={() => navigate('/register')} style={{
              marginTop: 24, background: 'var(--navy)', color: 'white', border: 'none',
              padding: '12px 24px', borderRadius: 8, cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', gap: 6
            }}>
              List Your Space <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Features ── */}
      <div style={{ padding: '100px 24px', background: 'var(--gray-50)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', fontWeight: 800, color: 'var(--navy)' }}>
              Everything You Need
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
            {[
              { icon: <MapPin size={22} />, title: 'Map-Based Search', desc: 'Find spaces visually on an interactive map of Dubai' },
              { icon: <BarChart3 size={22} />, title: 'Impressions Data', desc: 'See estimated daily views for every location' },
              { icon: <Shield size={22} />, title: 'Artwork Approval', desc: 'Media owners review and approve all creatives' },
              { icon: <Zap size={22} />, title: 'Fast Booking', desc: 'Request a booking in under 5 minutes' },
            ].map(f => (
              <div key={f.title} style={{ background: 'white', borderRadius: 14, padding: 24, border: '1px solid var(--gray-100)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(48,102,190,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tech)', marginBottom: 14 }}>
                  {f.icon}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 6, color: 'var(--navy)' }}>{f.title}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{
        padding: '100px 24px',
        background: 'linear-gradient(135deg, var(--navy) 0%, var(--royal) 100%)',
        textAlign: 'center'
      }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', fontWeight: 900, color: 'white', marginBottom: 16 }}>
          Ready to Get Started?
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.05rem', marginBottom: 36 }}>
          Join advertisers and media owners already using AdsLocate.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/register')} style={{
            background: 'white', color: 'var(--navy)', border: 'none',
            padding: '14px 36px', borderRadius: 10, cursor: 'pointer',
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1rem',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            Create Free Account <ArrowRight size={18} />
          </button>
          <button onClick={() => navigate('/login')} style={{
            background: 'rgba(255,255,255,0.1)', color: 'white',
            border: '1px solid rgba(255,255,255,0.25)', padding: '14px 36px',
            borderRadius: 10, cursor: 'pointer', fontFamily: 'var(--font-display)',
            fontWeight: 700, fontSize: '1rem'
          }}>
            Sign In
          </button>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ background: 'var(--gray-900)', padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 800, color: 'white', marginBottom: 8 }}>
          <span style={{ color: 'white' }}>Ads</span><span style={{ color: 'var(--tech)' }}>Locate</span>
        </div>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
          © 2026 AdsLocate. All Rights Reserved. Based in Dubai, UAE.
        </p>
      </footer>

    </div>
  )
}
